<script lang="ts">
    import { onDestroy, onMount, tick } from 'svelte';
    import { get } from 'svelte/store';

    import { getCardEndStickyBottomHeightPx } from '../constants/cookieLayout';
    import { END_CARD_CARD_BG, END_CARD_LINE } from '../constants/imgList';
    import { bottomButtonHeight, cookieHeight, isLogin } from '../store/index';
    import { handleKeyboardActivate } from '../utils/a11y';

    import CardEndBenefitNew from './CardEndBenefitNew.svelte';
    import CardEndStepModule from './CardEndStepModule.svelte';
    import PhoneInput from './PhoneInput.svelte';

    import webp from '@/actions/webp';

    let originalButton: HTMLDivElement | undefined;
    let inlinePhoneInput: PhoneInput | undefined;
    let bottomButtonVisible = false;
    let phoneButtonLoading = false;
    let phoneInputError = false;
    let observer: IntersectionObserver | undefined;

    const handleBottomConfirm = () => {
        inlinePhoneInput?.handleConfirmClick();
    };

    const syncCardEndStickyBottomHeight = () => {
        bottomButtonHeight.set(getCardEndStickyBottomHeightPx());
    };

    const showLoggedInBottomButton = () => {
        observer?.disconnect();
        bottomButtonVisible = true;
        syncCardEndStickyBottomHeight();
    };

    const observeConfirmButton = () => {
        if (!originalButton || $isLogin) {
            return;
        }

        observer?.disconnect();
        observer = new IntersectionObserver(
            ([entry]) => {
                bottomButtonVisible = !entry.isIntersecting;

                if (bottomButtonVisible) {
                    syncCardEndStickyBottomHeight();
                } else {
                    bottomButtonHeight.set(0);
                }
            },
            { threshold: 0.5 }
        );
        observer.observe(originalButton);
    };

    $: if ($isLogin) {
        showLoggedInBottomButton();
    }

    onMount(() => {
        if (get(isLogin)) {
            showLoggedInBottomButton();
        } else {
            tick().then(observeConfirmButton);
        }

        const unsubscribeLogin = isLogin.subscribe((loggedIn) => {
            if (loggedIn) {
                showLoggedInBottomButton();

                return;
            }

            bottomButtonVisible = false;
            bottomButtonHeight.set(0);
            tick().then(observeConfirmButton);
        });

        return () => {
            unsubscribeLogin();
            observer?.disconnect();
            bottomButtonHeight.set(0);
        };
    });

    onDestroy(() => {
        observer?.disconnect();
        bottomButtonHeight.set(0);
    });

    const handlePhoneInputErrorChange = (isError: boolean) => {
        phoneInputError = isError;
    };

</script>

<div
    class="login-module mkt-adv-container login-type-e mkt-adv-container-long is-long"
    class:login-module-e-logged={$isLogin}
>
    {#if $isLogin}
        <div class="logged-phone-input-host" aria-hidden="true">
            <PhoneInput
                bind:this={inlinePhoneInput}
                loginType="E"
                bind:buttonLoading={phoneButtonLoading}
                on:inputErrorChange={(event) =>
                    handlePhoneInputErrorChange(event.detail)}
            />
        </div>
    {:else}
        <div class="login-container login-container-e">
            <div class="login-card-bg" use:webp={END_CARD_CARD_BG}></div>
            <div class="login-content-e">
                <div class="login-title-e">
                    Ingresa tu número de celular
                    <div class="login-title-line" use:webp={END_CARD_LINE}></div>
                </div>
                <PhoneInput
                    bind:this={inlinePhoneInput}
                    loginType="E"
                    bind:confirmButton={originalButton}
                    bind:buttonLoading={phoneButtonLoading}
                    on:inputErrorChange={(event) =>
                        handlePhoneInputErrorChange(event.detail)}
                />
            </div>
        </div>
    {/if}
    <CardEndBenefitNew />
    <CardEndStepModule />
</div>

{#if bottomButtonVisible}
    <div
        class="bottom-btn-container"
        style={`bottom:${$cookieHeight}px;`}
    >
        <div
            role="button"
            tabindex="0"
            class="bottom-confirm-btn"
            class:confirm-btn--input-error={phoneInputError}
            class:loading={phoneButtonLoading}
            on:click={handleBottomConfirm}
            on:keydown={(event) =>
                handleKeyboardActivate(event, handleBottomConfirm)}
        >
            {#if phoneButtonLoading}
                <div class="loading-dots">
                    <span class="dot"></span>
                    <span class="dot"></span>
                    <span class="dot"></span>
                </div>
            {:else}
                <div class="confirm-btn-text">Continuar</div>
            {/if}
        </div>
    </div>
{/if}

<style lang="scss">
    .login-module.mkt-adv-container {
        width: 100%;
        box-sizing: border-box;
        position: relative;
        z-index: 9;
    }

    .login-module.mkt-adv-container-long {
        margin-bottom: -0.48rem;
    }

    .login-module.login-type-e {
        width: 100%;
        position: relative;
        z-index: 9;
        top: 0;
        padding: 0 0.32rem;
        margin: 0;
        background: transparent;
        box-sizing: border-box;
    }

    .login-module.mkt-adv-container-long.login-type-e,
    .login-module.login-type-e.is-long {
        /* 悬浮在 CardEndHeadArea two-bg 上，与 title-img 间距 0.14rem */
        margin-top: -25.7rem;
    }

    .login-module.mkt-adv-container-long.login-type-e.login-module-e-logged,
    .login-module.login-type-e.is-long.login-module-e-logged {
        /* 已登录无页内按钮：benefit 10.28 + step 6.02 */
        margin-top: -16.3rem;
    }

    .logged-phone-input-host {
        position: absolute;
        width: 0;
        height: 0;
        overflow: hidden;
        opacity: 0;
        pointer-events: none;
    }

    .login-container.login-container-e {
        position: relative;
        z-index: 1;
        width: 100%;
        margin: 0;
        padding: 0.4rem 0.32rem 0.24rem;
        box-sizing: border-box;
        border-radius: 0.32rem;
        overflow: hidden;
        min-height: 4.8rem;
    }

    .login-card-bg {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 0;
        background-repeat: no-repeat;
        background-size: 100% 100%;
        background-position: top center;
        pointer-events: none;
    }

    .login-content-e {
        position: relative;
        z-index: 1;
    }

    .login-title-e {
        margin-bottom: 0.24rem;
        font-family: DiDiSans-Pro-Bold;
        font-size: 0.28rem;
        line-height: 0.32rem;
        font-weight: 500;
        text-align: center;
        color: #ffffff;
    }

    .login-title-line {
        /* 设计稿 374px，现金贷 line 为 298px (3.2rem) */
        width: 3.74rem;
        height: 0.12rem;
        margin: -0.12rem auto 0;
        background-repeat: no-repeat;
        background-size: 100% 100%;
        background-position: center;
    }

    :global(.login-module.login-type-e .input-length-show),
    :global(.login-module.login-type-e .input-length-show span),
    :global(.login-module.login-type-e .input-length-show.is-input-complete),
    :global(.login-module.login-type-e .input-length-show.is-input-complete span) {
        color: #ffffff;
    }

    .bottom-btn-container {
        position: fixed;
        left: 50%;
        right: auto;
        transform: translateX(-50%);
        width: 7.5rem;
        max-width: 100%;
        z-index: 11;
        padding: 0.4rem 0.32rem 0.4rem;
        box-sizing: border-box;
        background: #1c0b00;
        pointer-events: none;
        transition: bottom 0.3s ease-in-out;
    }

    .bottom-confirm-btn {
        width: 100%;
        max-width: 100%;
        height: 1.2rem;
        border-radius: var(--Gone-radius-M, 0.32rem);
        background: #ff3800;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto;
        cursor: pointer;
        box-shadow: 0 -0.04rem 0.2rem rgba(0, 0, 0, 0.15);
        pointer-events: auto;

        &.confirm-btn--input-error {
            background: #644433;

            .confirm-btn-text {
                color: #937c70;
            }
        }
    }

    @supports (color: color(display-p3 1 1 1)) {
        .bottom-confirm-btn {
            background: color(display-p3 1 0.3725 0.0784);
        }

        .bottom-confirm-btn.confirm-btn--input-error {
            background: color(display-p3 0.3721 0.2723 0.21);

            .confirm-btn-text {
                color: color(display-p3 0.5608 0.4902 0.4471);
            }
        }
    }

    .bottom-confirm-btn .confirm-btn-text {
        display: flex;
        justify-content: center;
        align-items: center;
        color: #ffffff;
        text-align: center;
        font-family: DiDiSans-Pro-ExtraBold;
        font-weight: 800;
        font-size: 0.48rem;
        line-height: 0.64rem;
        letter-spacing: 0;
    }

    .loading-dots {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.1rem;
    }

    .loading-dots .dot {
        width: 0.16rem;
        height: 0.16rem;
        background: #ffffff;
        border-radius: 50%;
        opacity: 0.5;
        animation: opacity-change 0.9s ease-in-out infinite;
    }

    .loading-dots .dot:nth-child(2) {
        animation-delay: 0.3s;
    }

    .loading-dots .dot:nth-child(3) {
        animation-delay: 0.6s;
    }

    @keyframes opacity-change {
        0%,
        100% {
            opacity: 0.5;
        }

        50% {
            opacity: 1;
        }
    }
</style>
